import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const SuperadminReviews = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');

    const [reviews, setReviews] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('Pending'); // 'Pending', 'Approved', 'Rejected'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchReviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchReviews = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_all_reviews`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (data.success) {
                setReviews(data.reviews);
            } else {
                setError(data.message || 'Failed to fetch reviews');
            }
        } catch (err) {
            setError('Error connecting to dashboard server');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (reviewId, newStatus) => {
        setError('');
        setSuccessMessage('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=update_review_status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ review_id: reviewId, status: newStatus })
            });
            const data = await res.json();
            if (data.success) {
                setSuccessMessage(data.message || `Review updated successfully to ${newStatus}`);
                fetchReviews();
            } else {
                setError(data.message || 'Failed to update review status');
            }
        } catch (err) {
            setError('Error connecting to dashboard server');
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this review permanently?')) return;
        setError('');
        setSuccessMessage('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ review_id: reviewId })
            });
            const data = await res.json();
            if (data.success) {
                setSuccessMessage(data.message || 'Review deleted successfully');
                fetchReviews();
            } else {
                setError(data.message || 'Failed to delete review');
            }
        } catch (err) {
            setError('Error connecting to dashboard server');
        }
    };

    const filteredReviews = reviews
        .filter(r => r.status === activeTab)
        .filter(r =>
            r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.role && r.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
            r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.phone.includes(searchTerm) ||
            r.comment.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const renderStars = (rating) => {
        return Array.from({ length: 5 }).map((_, i) => (
            <i
                key={i}
                className={`fas fa-star ${i < rating ? 'star-filled' : 'star-empty'}`}
                style={{ color: i < rating ? '#fbbf24' : 'var(--text-secondary)', marginRight: '3px' }}
            />
        ));
    };

    return (
        <SuperAdminLayout>
            <div className="sa-header-area">
                <div>
                    <h2 className="sa-page-title">Visitor Testimonials & Reviews</h2>
                    <p className="sa-page-subtitle">Moderate public feedback and reviews submitted by landing page visitors.</p>
                </div>
            </div>

            {error && <div className="sa-alert sa-alert-error" style={{ marginBottom: '1.5rem' }}><i className="fas fa-exclamation-circle"></i> {error}</div>}
            {successMessage && <div className="sa-alert sa-alert-success" style={{ marginBottom: '1.5rem' }}><i className="fas fa-check-circle"></i> {successMessage}</div>}

            {/* Tab navigation */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: '20px', gap: '10px' }}>
                {['Pending', 'Approved', 'Rejected'].map(tab => {
                    const count = reviews.filter(r => r.status === tab).length;
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '12px 20px',
                                background: 'none',
                                border: 'none',
                                color: activeTab === tab ? 'var(--primary-color)' : 'var(--text-secondary)',
                                borderBottom: activeTab === tab ? '2px solid var(--primary-color)' : '2px solid transparent',
                                fontWeight: activeTab === tab ? 700 : 500,
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            {tab} Approval ({count})
                        </button>
                    );
                })}
            </div>

            <div className="sa-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                    <div className="sa-search-box" style={{ margin: 0, flex: 1, maxWidth: '400px' }}>
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search reviews..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="sa-input"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <button className="sa-secondary-btn" onClick={fetchReviews}><i className="fas fa-sync"></i> Refresh</button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <i className="fas fa-spinner fa-spin fa-2x" style={{ color: 'var(--primary-color)' }}></i>
                        <p style={{ marginTop: '10px', color: 'var(--text-secondary)' }}>Loading reviews...</p>
                    </div>
                ) : filteredReviews.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        <i className="fas fa-comment-slash fa-3x" style={{ marginBottom: '15px', opacity: 0.3 }}></i>
                        <p>No reviews found in this category.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Rating & Comment</th>
                                    <th>Reviewer Details</th>
                                    <th>Submitted At</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredReviews.map((review) => (
                                    <tr key={review.id}>
                                        <td style={{ verticalAlign: 'top', minWidth: '320px' }}>
                                            <div style={{ marginBottom: '8px' }}>
                                                {renderStars(review.rating)}
                                            </div>
                                            <div style={{ fontStyle: 'italic', color: 'var(--text-primary)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                                                "{review.comment}"
                                            </div>
                                        </td>
                                        <td style={{ verticalAlign: 'top', minWidth: '220px' }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{review.name}</div>
                                            {review.role && <div style={{ fontSize: '0.8rem', color: 'var(--primary-color)', marginTop: '2px' }}>{review.role}</div>}

                                            {/* Admin sees real email & phone, but public fetch anonymizes this */}
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '6px' }}>
                                                <i className="fas fa-envelope" style={{ marginRight: '6px', width: '14px' }}></i>
                                                <a href={`mailto:${review.email}`} style={{ color: 'inherit' }}>{review.email}</a>
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                <i className="fas fa-phone" style={{ marginRight: '6px', width: '14px' }}></i>
                                                <a href={`https://wa.me/${review.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                                    {review.phone}
                                                </a>
                                            </div>
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                            {new Date(review.created_at).toLocaleDateString(undefined, {
                                                year: 'numeric', month: 'short', day: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            })}
                                        </td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'top', minWidth: '150px' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                {review.status !== 'Approved' && (
                                                    <button
                                                        className="sa-action-btn success"
                                                        onClick={() => handleUpdateStatus(review.id, 'Approved')}
                                                        title="Approve Review"
                                                        style={{ padding: '8px 12px', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        <i className="fas fa-check"></i> Approve
                                                    </button>
                                                )}
                                                {review.status !== 'Rejected' && (
                                                    <button
                                                        className="sa-action-btn warning"
                                                        onClick={() => handleUpdateStatus(review.id, 'Rejected')}
                                                        title="Reject Review"
                                                        style={{ padding: '8px 12px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        <i className="fas fa-times"></i> Reject
                                                    </button>
                                                )}
                                                <button
                                                    className="sa-action-btn delete"
                                                    onClick={() => handleDeleteReview(review.id)}
                                                    title="Delete Review Permanently"
                                                    style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default SuperadminReviews;
